/* eslint-disable no-empty-pattern */
import { memo, useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useStateAuthValue } from "../context/AuthState";
import { setSidebarItems } from "../utils/sibebarPaths";
import SidebarSubItem, { isPathActive } from "./SidebarSubItem";
import Colors from "../theme/Colors";
import Logo from "../assets/images/logo.png";
import Navbar from "./Navbar";
import pkg from "../../package.json";
import { useToast } from "../context/Toast/ToastProvider";
import { showToast, ToastType } from "../utils/ToastUtil";
import { useStateReportValue } from "../context/Reports";
import { useRecordRecentSection } from "../hooks/useRecordRecentSection";
import { clearRecentSections } from "../utils/recentSections";

const Item = memo(
  ({ title, path, icon, action, subItems, setIsSidebarOpen }) => {
    const [{}, dispatch] = useStateAuthValue();

    let size = subItems ? Object.keys(subItems).length : 0;
    const navigate = useNavigate();
    const location = useLocation();
    // El resaltado activo se deriva de la URL: el ítem con `path` propio matchea
    // directo; el padre con sub-ítems queda activo si alguno de sus hijos lo está.
    const active = path
      ? isPathActive(path, location)
      : subItems?.some((subItem) => isPathActive(subItem.path, location));
    return size !== 0 ? (
      <SubMenu
        label={title}
        key={title}
        rootStyles={{ fontSize: "1rem !important" }}
        active={active}
        className="menu-item"
        onClick={() => {
          if (action === "LOG_OUT") {
            dispatch({
              type: "LOG_OUT",
            });
            localStorage.removeItem("token");
            // Limpia los accesos recientes para no filtrarlos entre usuarios
            // en una PC compartida.
            clearRecentSections();
          }
          if (!subItems?.length) {
            navigate(path);
            window.scrollTo({
              top: 0,
              behavior: "smooth", // para un desplazamiento suave
            });
          }
        }}
        icon={icon}
      >
        {subItems?.map((subItem) => (
          <SidebarSubItem
            key={subItem.title}
            title={subItem.title}
            path={subItem.path}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        ))}
      </SubMenu>
    ) : (
      <Box
        sx={{
          "& .ps-menu-label": {
            overflow: "inherit!important",
          },
        }}
      >
        <MenuItem
          key={title}
          active={active}
          className="menu-item"
          // Si es una ruta real (no una acción tipo LOG_OUT), se renderiza como
          // <Link> (anchor con href) para soportar click derecho → nueva
          // pestaña/ventana, Ctrl/⌘+click y click del medio.
          component={path && action !== "LOG_OUT" ? <Link to={path} /> : undefined}
          onClick={(e) => {
            // Modificadores → dejar que el navegador abra una nueva pestaña/ventana.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            // Las acciones (sin ruta real) navegan a mano; los <Link> ya navegan solos.
            if (!(path && action !== "LOG_OUT")) navigate(path);
            setIsSidebarOpen(false);
            window.scrollTo({
              top: 0,
              behavior: "smooth", // para un desplazamiento suave
            });
          }}
          icon={icon}
        >
          <Typography>{title}</Typography>
        </MenuItem>
      </Box>
    );
  }
);

const SidebarLayout = ({ children }) => {
  const [{ userToken, userRole }] = useStateAuthValue();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarClassName, setSidebarClassName] = useState("sidebar");
  const toastDispatch = useToast();
  const [items, setItems] = useState([]);
  const displayError = (msg) =>
    showToast(toastDispatch, msg, ToastType.ERROR, ToastType.ERROR);
  const [{}, dispatch] = useStateReportValue();

  // Registra la sección actual como acceso reciente (para el Home).
  useRecordRecentSection(userRole);

  useEffect(() => {
    if (userToken) {
      const getItems = async () => {
        const items = await setSidebarItems(
          userRole,
          userToken,
          displayError,
          dispatch
        );
        setItems(items);
      };
      getItems();
    }
  }, [userToken]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1300) {
        setSidebarClassName("sidebar sidebar-is-closed");
      } else {
        setSidebarClassName("sidebar sidebar-is-open");
      }
    }

    if (userToken) {
      handleResize();
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [userToken]);

  return (
    <div className="layout">
      <Box
        sx={{
          "& .ps-menu-button ": {
            borderRadius: "10px",
            margin: "5px",
            transition: "0.2s ease-in-out all",
          },
          "& .ps-menu-button:hover ": {
            background: `${Colors.AzulMedilineClaro} !important`,
          },
          "& .ps-menu-button:active ": {
            background: `linear-gradient(195deg, ${Colors.AzulMediline}, ${Colors.AzulMedilineClaro}) !important`,
          },
          // Ítem seleccionado: resaltado persistente con el gradiente de marca
          "& .ps-menuitem-root.ps-active > .ps-menu-button ": {
            background: `linear-gradient(195deg, ${Colors.AzulMediline}, ${Colors.AzulMedilineClaro}) !important`,
            color: "#fff !important",
          },
          "& .ps-menu-root": {
            minHeight: "700px",
          },
        }}
      >
        <Sidebar
          className={
            isSidebarOpen ? "sidebar  sidebar-is-open" : sidebarClassName
          }
        >
          <Menu>
            <Box mb="25px">
              <Box
                sx={{
                  position: "absolute",
                  left: "81%",
                  top: "1%",
                }}
              >
                <ClearIcon
                  onClick={() => {
                    setIsSidebarOpen(false);
                  }}
                  className="navbar-open-sidebar"
                />
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{ margin: "20px" }}
              >
                <img
                  alt="brand"
                  width="100px"
                  height="100px"
                  src={Logo}
                  style={{ cursor: "pointer", height: "65px" }}
                />
              </Box>
            </Box>
            <hr className="faded-out-hr" />
            {items?.map((item) => {
              return (
                <Item
                  key={item.title}
                  title={item.title}
                  path={item.path}
                  icon={item.icon}
                  action={item?.action}
                  subItems={item.subItems}
                  setIsSidebarOpen={setIsSidebarOpen}
                />
              );
            })}
          </Menu>
        </Sidebar>
      </Box>
      <div className="container">
        <Navbar
          setIsSidebarOpen={setIsSidebarOpen}
          isSidebarOpen={isSidebarOpen}
        />
        <div>{children}</div>
        {pkg && (
          <div style={{ float: "right", marginTop: "35px" }}>
            <p
              style={{
                fontWeight: "700",
                fontSize: "11px",
              }}
            >
              Versión {pkg?.version}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarLayout;
